import type { IntlShape } from "@formatjs/intl";
import { Card, Col, DatePicker, Form, Input, Row, Select } from "antd";
import type { Client } from "../generated-axios-client";
import { PROJECT_STATUS_LABELS } from "../project/projectConstants";

type ProjectFieldsProps = {
  intl: IntlShape;
  clientItems: Client[];
};

const projectStatusOptions = Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export const ProjectFields = ({ intl, clientItems }: ProjectFieldsProps) => {
  return (
    <Card title={intl.formatMessage({ id: "project_section_details" })}>
      <Row gutter={24}>
        <Col xs={24} md={12}>
          <Form.Item
            name="name"
            label={intl.formatMessage({ id: "project_name" })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "validation_message_project_name" }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({ id: "project_field_name_placeholder" })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="project_status"
            label={intl.formatMessage({ id: "project_status" })}
          >
            <Select
              allowClear
              placeholder={intl.formatMessage({ id: "project_field_status_placeholder" })}
              options={projectStatusOptions}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} md={12}>
          <Form.Item
            name="start"
            label={intl.formatMessage({ id: "project_start_date" })}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="end"
            label={intl.formatMessage({ id: "project_end_date" })}
            dependencies={["start"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue("start");
                  if (!value || !start || value.isAfter(start)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(intl.formatMessage({ id: "validation_message_end_after_start" }))
                  );
                },
              }),
            ]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} md={12}>
          <Form.Item
            name="client"
            label={intl.formatMessage({ id: "project_client" })}
          >
            <Select
              showSearch
              allowClear
              placeholder={intl.formatMessage({ id: "project_field_client_placeholder" })}
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={clientItems.map((c) => ({ label: c.legal_name, value: c.id }))}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
